import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Category } from 'services/product.service';
import { SortOption } from 'components/sections/shop/utils/constants';
import { findCategoryBySlug, createSlug } from 'utils/HelperFn';

interface UseProductFiltersReturn {
  categoryName: string;
  searchQuery: string;
  sortBy: SortOption;
  currentPage: number;
  selectedCategory: Category | undefined;
  updateFilter: (key: string, value: string, isMulti?: boolean) => void;
  loadMoreProducts: () => void;
  clearAllFilters: () => void;
  setCategory: (categoryName: string) => void;
}

/**
 * Custom hook to manage product filter state from URL search params
 */
export const useProductFilters = (categories: Category[]): UseProductFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current filters from URL
  const categorySlug = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';
  const sortBy = (searchParams.get('sort') as SortOption) || 'latest';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Find selected category by slug - memoized to prevent recalculation
  const selectedCategory = useMemo(() => {
    if (!categorySlug) return undefined;
    return findCategoryBySlug(categorySlug, categories);
  }, [categories, categorySlug]);

  // Get category name for API calls (backward compatibility)
  const categoryName = selectedCategory?.name || '';

  // Update URL with new filter - memoized with useCallback
  const updateFilter = useCallback(
    (key: string, value: string, isMulti = false) => {
      setSearchParams((prev) => {
        const newSearchParams = new URLSearchParams(prev);

        if (isMulti) {
          const currentValues = newSearchParams.getAll(key);
          if (currentValues.includes(value)) {
            newSearchParams.delete(key);
            currentValues.filter((v) => v !== value).forEach((v) => newSearchParams.append(key, v));
          } else {
            newSearchParams.append(key, value);
          }
        } else {
          if (value) {
            newSearchParams.set(key, value);
          } else {
            newSearchParams.delete(key);
          }
        }

        // Remove search parameter when category is selected
        // Convert category name to slug if it's a category name
        if (key === 'category' && value) {
          newSearchParams.delete('search');
          newSearchParams.delete('mould');
          newSearchParams.delete('shade');
          // If value is a category name (not already a slug), convert it
          const category = categories.find((cat) => cat.name === value);
          if (category) {
            newSearchParams.set(key, createSlug(category.name));
          } else {
            // Already a slug or not found, use as-is
            newSearchParams.set(key, value);
          }
        }

        // Reset to page 1 when filters change
        newSearchParams.set('page', '1');
        return newSearchParams;
      });
    },
    [setSearchParams, categories]
  );

  // Load more products (next page) - memoized with useCallback
  const loadMoreProducts = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', (currentPage + 1).toString());
      return newParams;
    });
  }, [currentPage, setSearchParams]);

  // Clear all filters - memoized with useCallback
  const clearAllFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Set category - memoized with useCallback
  // Removes search parameter when category is selected
  // Accepts either category name or slug, converts to slug for URL
  const setCategory = useCallback(
    (categoryNameOrSlug: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        // Find category by name or slug, then use slug for URL
        const category = categories.find(
          (cat) => cat.name === categoryNameOrSlug || createSlug(cat.name) === categoryNameOrSlug.toLowerCase()
        );
        const slug = category ? createSlug(category.name) : createSlug(categoryNameOrSlug);
        newParams.set('category', slug);
        newParams.delete('search'); // Remove search when category is selected
        newParams.delete('mould');
        newParams.delete('shade');
        newParams.set('page', '1'); // Reset to page 1
        return newParams;
      }, { replace: true });
    },
    [setSearchParams, categories]
  );

  return {
    categoryName,
    searchQuery,
    sortBy,
    currentPage,
    selectedCategory,
    updateFilter,
    loadMoreProducts,
    clearAllFilters,
    setCategory,
  };
};

